import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { isPrestataireRole } from '../utils/roleUtils';

// Cles de persistance des compteurs : la derniere valeur connue (calculee par les pages) survit
// au Cmd+R, pour que les pastilles ne disparaissent pas tant que le rafraichissement de fond n'a
// pas repondu, et qu'elles ne dependent plus d'une visite de la page proprietaire.
const LS = { commandes: 'notif_unread_commandes', operations: 'notif_unread_operations', candidatures: 'notif_unread_candidatures' };
const readCount = (key: string): number => {
  const v = localStorage.getItem(key);
  const n = v != null ? Number(v) : 0;
  return Number.isFinite(n) ? n : 0;
};

interface NotificationsContextType {
  unreadCommandes: number;
  setUnreadCommandes: (count: number) => void;
  unreadOperations: number;
  setUnreadOperations: (count: number) => void;
  unreadCandidatures: number;
  setUnreadCandidatures: (count: number) => void;
  // Recharge les compteurs a la demande (login + a chaque navigation), sans reset prealable.
  refresh: () => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // Initialise depuis la derniere valeur persistee (restauration immediate au Cmd+R).
  const [unreadCommandes, setUnreadCommandes] = useState(() => readCount(LS.commandes));
  const [unreadOperations, setUnreadOperations] = useState(() => readCount(LS.operations));
  const [unreadCandidatures, setUnreadCandidatures] = useState(() => readCount(LS.candidatures));

  // Persiste chaque compteur a chaque changement (page ou refresh) -> survit au rechargement.
  useEffect(() => { localStorage.setItem(LS.commandes, String(unreadCommandes)); }, [unreadCommandes]);
  useEffect(() => { localStorage.setItem(LS.operations, String(unreadOperations)); }, [unreadOperations]);
  useEffect(() => { localStorage.setItem(LS.candidatures, String(unreadCandidatures)); }, [unreadCandidatures]);

  // Ne remet a zero QUE lors d'un changement de compte (pas a chaque montage / Cmd+R).
  const prevUserIdRef = useRef<string | number | null | undefined>(undefined);

  // Recharge les compteurs selon le role. Appelable a la demande : utilise au login (effet ci-dessous)
  // ET a chaque navigation (depuis la sidebar) -> les pastilles ne restent plus figees jusqu'a une
  // visite de l'onglet Profil.
  const refresh = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || !user) return;

    // Candidatures + commandes pour TOUS les prestataires (y compris "Prestataire signataire admin"),
    // sinon le refresh tapait l'endpoint acheteur (operations) -> compteurs prestataire jamais charges.
    const isPrestataire = isPrestataireRole(user.role);
    if (isPrestataire) {
      fetch(`${process.env.REACT_APP_API_URL}/candidatures`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((data) => {
          const list = Array.isArray(data?.data) ? data.data : [];
          setUnreadCandidatures(list.filter((c: any) => c.statut === 'brouillon').length);
        })
        .catch(() => { });

      // Commandes (compteur action requise + paniers importés)
      fetch(`${process.env.REACT_APP_API_URL}/commandes-compteur`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
        .then((r) => r.json())
        .then((result) => {
          if (result.status === 'success' && result.data) {
            const count = result.data.count ?? 0;
            const countBDCI = result.data.count_bdci ?? 0;
            setUnreadCommandes(count + countBDCI);
          }
        })
        .catch(() => { });
    } else {
      // Opérations — acheteurs uniquement. Meme URL que la page Operations (fetchActionRequiseCount) :
      // filtre "Action requise" (status_action=1) -> la pastille est identique en nav et sur la page.
      fetch(`${process.env.REACT_APP_API_URL}/operations-compteur?status_action=1`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
        .then((r) => r.json())
        .then((result) => {
          if (result.status === 'success' && result.data) {
            // Total = operations action requise (BC + MS) + paniers importes (BCI), comme la page Operations.
            setUnreadOperations((result.data.count ?? 0) + (result.data.count_bdci ?? 0));
          }
        })
        .catch(() => { });
    }
  }, [user]);

  // Recharge a chaque changement de user. Reset des pastilles UNIQUEMENT lors d'un vrai changement
  // de compte (pas au 1er montage / Cmd+R) -> sinon on effacerait la valeur persistee restauree.
  useEffect(() => {
    const account = user?.email ?? null;
    if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== account) {
      setUnreadCommandes(0);
      setUnreadOperations(0);
      setUnreadCandidatures(0);
    }
    prevUserIdRef.current = account;
    refresh();
  }, [user, refresh]);

  return (
    <NotificationsContext.Provider value={{
      unreadCommandes,
      setUnreadCommandes,
      unreadOperations,
      setUnreadOperations,
      unreadCandidatures,
      setUnreadCandidatures,
      refresh,
    }}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};